import React, { useMemo } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { COLORS } from '../constants/theme';

interface DailyUsageChartPoint {
  readonly date: string;
  readonly value: number;
}

interface DailyUsageChartProps {
  title: string;
  points: DailyUsageChartPoint[];
  formatValue?: (value: number) => string;
  height?: number;
}

export const DailyUsageChart: React.FC<DailyUsageChartProps> = ({
  title,
  points,
  formatValue,
  height = 88,
}) => {
  const maxValue = useMemo(() => {
    return points.reduce((max, point) => Math.max(max, point.value), 0);
  }, [points]);

  if (points.length === 0) {
    return null;
  }

  const lastIndex = points.length - 1;
  const tickIndexes = new Set([
    0,
    Math.floor(lastIndex / 3),
    Math.floor((lastIndex * 2) / 3),
    lastIndex,
  ]);

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title}>{title}</Text>
        {maxValue > 0 && formatValue ? (
          <Text style={styles.peak}>Peak {formatValue(maxValue)}</Text>
        ) : null}
      </View>
      <View style={[styles.bars, { height }]}>
        {points.map((point) => {
          const ratio = maxValue > 0 ? point.value / maxValue : 0;
          const barHeight = point.value > 0 ? Math.max(4, ratio * height) : 0;
          return (
            <View key={point.date} style={[styles.barTrack, { height }]}>
              <View style={[styles.barFill, { height: barHeight }]} />
            </View>
          );
        })}
      </View>
      <View style={styles.ticks}>
        {points.map((point, index) => (
          <View key={`tick-${point.date}`} style={styles.tickSlot}>
            {tickIndexes.has(index) ? (
              <Text style={styles.tick}>{Number(point.date.slice(8, 10))}</Text>
            ) : null}
          </View>
        ))}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: 16,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  title: {
    fontSize: 13,
    fontWeight: '600',
    color: COLORS.textSecondary,
  },
  peak: {
    fontSize: 12,
    color: COLORS.textSecondary,
  },
  bars: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 2,
  },
  barTrack: {
    flex: 1,
    justifyContent: 'flex-end',
    backgroundColor: COLORS.lightGray,
    borderRadius: 2,
    overflow: 'hidden',
  },
  barFill: {
    width: '100%',
    backgroundColor: COLORS.primary,
    borderRadius: 2,
  },
  ticks: {
    flexDirection: 'row',
    marginTop: 4,
    gap: 2,
  },
  tickSlot: {
    flex: 1,
    alignItems: 'center',
  },
  tick: {
    fontSize: 9,
    color: COLORS.textSecondary,
  },
});
